import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/utils/apiClient";
import { useAuth } from "@/state/auth";

export type AuditStatus = "ok" | "blocked" | "error";

/** One row of `copilot_audit_entries`. Mirrors the table created in
 *  `api/services/audit_log.py` and returned by `GET /api/admin/audit-logs`. */
export interface AuditEntry {
  id: number;
  session_id: string | null;
  user_id: string | null;
  role: string | null;
  page_context: string | null;
  message_preview: string | null;
  tools_called: string[] | null;
  status: AuditStatus;
  blocked_reason: string | null;
  provider: string | null;
  latency_ms: number | null;
  created_at: string;
}

export interface AuditLogFilters {
  status?: AuditStatus;
  role?: string;
  user_id?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface AuditLogResponse {
  entries: AuditEntry[];
  /** NOTE: the API returns the length of THIS page, not a grand total —
   *  see `get_audit_logs` in api/routes/admin.py. Pagination is therefore
   *  next/prev only; there is no "page X of Y" to show. */
  count: number;
  offset: number;
  limit: number;
}

const buildQuery = (filters?: AuditLogFilters): string => {
  if (!filters) return "";
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.role) params.set("role", filters.role);
  if (filters.user_id) params.set("user_id", filters.user_id);
  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  if (filters.limit != null) params.set("limit", String(filters.limit));
  if (filters.offset != null) params.set("offset", String(filters.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

const fetchAuditLogs = async (
  filters?: AuditLogFilters
): Promise<AuditLogResponse> =>
  apiRequest<AuditLogResponse>(`/api/admin/audit-logs${buildQuery(filters)}`);

export const useAuditLogs = (filters?: AuditLogFilters) => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: ["audit-logs", "list", filters ?? {}],
    queryFn: () => fetchAuditLogs(filters),
    staleTime: 15_000,
    enabled: Boolean(profile),
  });
};
